// File Name: TrustSection.jsx

import React from "react";

import { motion } from "framer-motion";

import {
  ShieldCheck,
  BrainCircuit,
  Globe,
  Zap,
  Cloud,
  Headphones,
} from "lucide-react";

const TRUST_ITEMS = [
  {
    icon: ShieldCheck,
    title: "Enterprise Security",
    desc: "Secure, scalable systems engineered for modern enterprise infrastructure and digital resilience.",
  },
  {
    icon: BrainCircuit,
    title: "AI-Powered Innovation",
    desc: "Advanced AI automation and intelligent systems built to accelerate business growth.",
  },
  {
    icon: Globe,
    title: "Global Delivery",
    desc: "Distributed engineering teams delivering high-quality solutions across global markets.",
  },
  {
    icon: Zap,
    title: "Fast Execution",
    desc: "Agile workflows ensuring rapid development cycles with premium-quality delivery.",
  },
  {
    icon: Cloud,
    title: "Cloud Infrastructure",
    desc: "Scalable cloud-native architectures optimized for speed, reliability and growth.",
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    desc: "Dedicated technical support and continuous monitoring for uninterrupted operations.",
  },
];

export default function TrustSection() {
  return (
    <>
      {/* PREMIUM MOBILE KEYFRAMES */}

      <style>
        {`
          @keyframes trustFloat {

            0%,
            100% {
              transform:
                translateY(0px);
            }

            50% {
              transform:
                translateY(-8px);
            }
          }

          @keyframes trustGlow {

            0%,
            100% {
              opacity: 0.45;
            }

            50% {
              opacity: 0.9;
            }
          }

          @keyframes mobilePulse {

            0%,
            100% {
              transform: scale(1);
            }

            50% {
              transform: scale(1.03);
            }
          }

          .trust-mobile-card {

            animation:
              trustFloat 6s ease-in-out infinite;
          }

          .trust-mobile-glow {

            animation:
              trustGlow 5s ease-in-out infinite;
          }

          @media (max-width: 768px) {

            .trust-mobile-card {

              border-radius: 28px !important;

              padding: 1.6rem !important;

              backdrop-filter:
                blur(18px);

              animation:
                trustFloat 5s ease-in-out infinite;
            }

            .trust-mobile-card:hover {

              transform:
                translateY(-6px)
                scale(1.01) !important;
            }

            .trust-mobile-icon {

              width: 58px !important;

              height: 58px !important;

              border-radius: 18px !important;

              animation:
                mobilePulse 4s ease-in-out infinite;
            }

            .trust-mobile-heading {

              font-size: 2.3rem !important;

              line-height: 1.15 !important;
            }

            .trust-mobile-subtext {

              font-size: 1rem !important;

              line-height: 1.8 !important;
            }

            .trust-mobile-grid {

              gap: 1.25rem !important;
            }

            .trust-mobile-title {

              font-size: 1.35rem !important;
            }

            .trust-mobile-desc {

              font-size: 0.92rem !important;

              line-height: 1.8 !important;
            }

            .trust-mobile-topglow {

              width: 520px !important;

              height: 520px !important;

              filter:
                blur(110px) !important;

              opacity: 0.7;
            }

            .trust-mobile-sideglow {

              width: 280px !important;

              height: 280px !important;

              filter:
                blur(90px) !important;
            }
          }

          @media (max-width: 480px) {

            .trust-mobile-heading {

              font-size: 2rem !important;
            }

            .trust-mobile-card {

              padding: 1.4rem !important;
            }

            .trust-mobile-icon {

              width: 54px !important;

              height: 54px !important;
            }

            .trust-mobile-title {

              font-size: 1.2rem !important;
            }

            .trust-mobile-desc {

              font-size: 0.88rem !important;
            }
          }
        `}
      </style>

      <section
        className="
          relative
          py-24
          md:py-32
          overflow-hidden

          bg-gradient-to-b
          from-[#F8F4FB]
          via-white
          to-[#F4EEFA]
        "
      >

        {/* TOP GLOW */}

        <div
          className="
            trust-mobile-topglow

            absolute

            top-[-200px]
            left-1/2
            -translate-x-1/2

            w-[850px]
            h-[850px]

            bg-purple-300/20

            blur-[160px]

            rounded-full
          "
        />

        {/* LEFT GLOW */}

        <div
          className="
            trust-mobile-sideglow

            absolute

            left-[-120px]
            bottom-0

            w-[400px]
            h-[400px]

            bg-fuchsia-300/10

            blur-[120px]

            rounded-full
          "
        />

        {/* GRID BACKGROUND */}

        <div
          className="
            absolute
            inset-0

            grid-bg

            opacity-[0.03]
          "
        />

        <div
          className="
            relative

            max-w-7xl
            mx-auto

            px-5
            md:px-8
          "
        >

          {/* HEADING */}

          <div
            className="
              text-center

              max-w-3xl

              mx-auto

              mb-16
              md:mb-20
            "
          >

            <div
              className="
                text-xs

                uppercase

                tracking-[0.35em]

                text-purple-600

                font-bold

                mb-5
              "
            >

              WHY CHOOSE US

            </div>

            <h2
              className="
                trust-mobile-heading

                text-4xl
                md:text-6xl

                font-bold

                text-slate-900

                leading-tight
              "
              style={{
                fontFamily: "Sora",
              }}
            >

              Why businesses trust
              <br />

              <span
                className="
                  relative

                  inline-block

                  text-purple-700
                "
              >

                CodeFix Solutions

                <span
                  className="
                    trust-mobile-glow

                    absolute

                    inset-x-0
                    bottom-2

                    h-5

                    bg-purple-300/40

                    blur-2xl

                    -z-10
                  "
                />

              </span>

            </h2>

            <p
              className="
                trust-mobile-subtext

                mt-7

                text-lg

                leading-relaxed

                text-slate-600
              "
            >

              We deliver enterprise-grade technology
              solutions combining innovation,
              security, scalability and premium
              engineering excellence.

            </p>

          </div>

          {/* CARDS */}

          <div
            className="
              trust-mobile-grid

              grid
              grid-cols-1
              md:grid-cols-2
              lg:grid-cols-3

              gap-7
            "
          >

            {TRUST_ITEMS.map((item, i) => {

              const Icon = item.icon;

              return (
                <motion.div
                  key={i}

                  initial={{
                    opacity: 0,
                    y: 30,
                  }}

                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}

                  viewport={{
                    once: true,
                  }}

                  transition={{
                    duration: 0.55,
                    delay: i * 0.08,
                  }}

                  className="
                    trust-mobile-card

                    group

                    relative

                    overflow-hidden

                    rounded-[32px]

                    border
                    border-purple-100/60

                    bg-white/70

                    backdrop-blur-2xl

                    p-8

                    shadow-[0_10px_40px_rgba(120,70,180,0.08)]

                    hover:shadow-[0_30px_70px_rgba(120,70,180,0.16)]

                    hover:-translate-y-3

                    transition-all
                    duration-500
                  "
                >

                  {/* CARD GLOW */}

                  <div
                    className="
                      absolute

                      top-0
                      right-0

                      w-40
                      h-40

                      bg-purple-300/10

                      rounded-full

                      blur-3xl

                      opacity-0

                      group-hover:opacity-100

                      transition-all
                      duration-500
                    "
                  />

                  {/* ICON */}

                  <div
                    className="
                      trust-mobile-icon

                      relative

                      w-16
                      h-16

                      rounded-2xl

                      bg-gradient-to-br
                      from-[#6D3B8C]
                      via-[#8B5CF6]
                      to-[#C084FC]

                      flex
                      items-center
                      justify-center

                      mb-6

                      shadow-lg
                      shadow-purple-500/20

                      group-hover:scale-110

                      transition-all
                      duration-500
                    "
                  >

                    <Icon
                      className="
                        w-7
                        h-7

                        text-white
                      "
                    />

                  </div>

                  {/* TITLE */}

                  <h3
                    className="
                      trust-mobile-title

                      text-2xl

                      font-bold

                      text-slate-900

                      mb-3
                    "
                    style={{
                      fontFamily: "Sora",
                    }}
                  >

                    {item.title}

                  </h3>

                  {/* DESC */}

                  <p
                    className="
                      trust-mobile-desc

                      text-slate-600

                      leading-relaxed

                      text-sm
                    "
                  >

                    {item.desc}

                  </p>

                </motion.div>
              );
            })}

          </div>

        </div>
      </section>
    </>
  );
}